import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { StoreHelper } from '../helpers/store.helper';
import { HeaderService } from '../services/header.service';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private headerService: HeaderService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Don't intercept the refresh token request itself to avoid infinite loops
    if (req.url.includes('user/refreshToken')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (this.isTokenExpiredError(error)) {
            return this.handle401Error(req, next);
          } else {
            // If we get a 401 that isn't explicitly a 'token_expired' we can refresh, 
            // the session is invalid and we must log the user out.
            this.logout();
          }
        }
        return throwError(() => error);
      })
    );
  }

  private isTokenExpiredError(error: HttpErrorResponse): boolean {
    try {
      const body = error.error;
      if (typeof body === 'string') {
        return body.includes('token_expired');
      }
      if (body && body.message) {
        return body.message === 'token_expired';
      }
    } catch (e) {}
    return false;
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const baseUrl = environment.apiUrl + '/ub2/';

      // No need to send refreshToken in body — it's in the HttpOnly cookie
      return this.http.post<any>(baseUrl + 'user/refreshToken', {}, { withCredentials: true }).pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;

          if (response && response.token) {
            // Update the in-memory token
            this.headerService.setToken(response.token);
            this.refreshTokenSubject.next(response.token);

            // Retry the original request with the new token
            return next.handle(this.addToken(req, response.token));
          }

          this.logout();
          return throwError(() => new Error('Refresh failed'));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.logout();
          return throwError(() => err);
        })
      );
    }

    // If already refreshing, queue this request until the new token is available
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addToken(req, token!)))
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer: ${token}`
      }
    });
  }

  private logout() {
    this.headerService.setToken(null);
    StoreHelper.clearUser();
    this.router.navigate(['/signin']);
  }
}
